import { useState } from "react";
import { useLogs, useStatus } from "../api/queries";
import { useClearOverride } from "../api/queries";
import PauseControls from "../components/PauseControls";
import EventList from "../components/EventList";
import OverrideModal from "../components/OverrideModal";
import ServerClock from "../components/ServerClock";
import { CheckCircle2, AlertTriangle, Circle, Clock, CalendarCheck, Server, Cpu, Palette, ScrollText } from "lucide-react";
import type { LogEvent } from "../api/types";

export default function StatusPage() {
  const { data, isLoading, isError } = useStatus();
  const logs = useLogs(50);
  const clearOverride = useClearOverride();
  const [overrideOpen, setOverrideOpen] = useState(false);

  if (isLoading) return <p className="text-sm text-slate-400">Loading…</p>;
  if (isError || !data) return <p className="text-sm text-rose-400">Failed to load status.</p>;

  const led = data.led;
  const matched = data.matched_event;
  const override = data.override;
  const hasColorOverride = !!(override && !override.paused);

  return (
    <div className="space-y-6">
      <ServerClock serverTimeTs={data.server.time_ts} timezone={data.server.timezone} />

      {/* Hero: current LED + active rule/event + Override button. */}
      <section className="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-6 sm:flex-row sm:items-center">
        <div
          className="flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-xl border border-slate-700"
          style={{
            backgroundColor: led?.effect === "off" ? "#0c0c0c" : led?.color || "#111",
            opacity: led ? (led.effect === "off" ? 0.4 : (led.luminance || 0) / 100 || 1) : 0.3,
          }}
          aria-label="LED preview"
        />
        <div className="flex-1 space-y-1">
          <div className="text-xs uppercase tracking-wide text-slate-400">Current LED</div>
          <div className="font-mono text-xl">
            {led ? `${led.color} · lum ${led.luminance} · ${led.effect}` : "no state yet"}
          </div>
          <div className="text-sm text-slate-400">
            {matched ? (
              <>
                <span className="text-slate-200">{matched.rule}</span>
                {matched.event_title ? ` · ${matched.event_title}` : ""}
              </>
            ) : (
              "no matched event"
            )}
          </div>
          {led ? (
            <div className="text-xs text-slate-500">applied {formatRelative(led.seconds_ago)} ago</div>
          ) : null}
        </div>
        <div className="flex flex-col gap-2 sm:items-end">
          <button
            type="button"
            onClick={() => setOverrideOpen(true)}
            className="btn-primary flex items-center gap-1"
          >
            <Palette className="h-4 w-4" />
            {hasColorOverride ? "Change override" : "Override color"}
          </button>
          {hasColorOverride ? (
            <button
              type="button"
              onClick={() => clearOverride.mutate()}
              disabled={clearOverride.isPending}
              className="btn-secondary text-xs"
            >
              {clearOverride.isPending ? "Clearing…" : "Cancel override"}
            </button>
          ) : null}
        </div>
      </section>

      {hasColorOverride ? (
        <section className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
          <span
            className="inline-block h-6 w-6 rounded-md border border-amber-500/40"
            style={{
              backgroundColor: override!.effect === "off" ? "#000" : override!.color,
              opacity: override!.effect === "off" ? 0.4 : override!.luminance / 100,
            }}
          />
          <span>
            Override active: {override!.color} · lum {override!.luminance} · {override!.effect}
            {override!.expires_ts
              ? ` · expires ${new Date(override!.expires_ts * 1000).toLocaleTimeString()}`
              : " · no expiry"}
          </span>
        </section>
      ) : null}

      <PauseControls override={override} />

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard
          icon={<Cpu className="h-4 w-4" />}
          label="Meross"
          value={
            data.meross.circuit_open
              ? "Circuit open"
              : data.meross.ready
                ? "Connected"
                : "Disconnected"
          }
          tone={data.meross.circuit_open ? "danger" : data.meross.ready ? "ok" : "warn"}
          sub={data.server.dev_mode ? "dev mode (fake backend)" : undefined}
        />
        <StatusCard
          icon={<CalendarCheck className="h-4 w-4" />}
          label="Last calendar update"
          value={data.last_webhook ? `${formatRelative(data.last_webhook.seconds_ago)} ago` : "never"}
          tone={!data.last_webhook ? "warn" : data.last_webhook.is_stale ? "danger" : "ok"}
          sub={
            data.last_webhook
              ? `${data.last_webhook.reason} · ${new Date(
                  data.last_webhook.received_ts * 1000,
                ).toLocaleTimeString()}`
              : undefined
          }
        />
        <StatusCard
          icon={<Cpu className="h-4 w-4" />}
          label={`Evaluator (every ${data.evaluator.interval_seconds}s)`}
          value={evaluatorDisplay(data.evaluator)}
          tone={evaluatorTone(data.evaluator)}
          sub={
            data.evaluator.last_tick_ts
              ? `last tick ${formatRelative(
                  Math.max(0, data.server.time_ts - data.evaluator.last_tick_ts),
                )} ago`
              : "no tick yet"
          }
        />
        <StatusCard
          icon={<Clock className="h-4 w-4" />}
          label="Uptime"
          value={formatUptime(data.server.uptime_s)}
          tone="ok"
          sub={`v${data.server.version}`}
        />
      </section>

      <section>
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
          <Server className="h-4 w-4" />
          Today&apos;s events
        </div>
        <EventList events={data.events} />
      </section>

      <section>
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
          <ScrollText className="h-4 w-4" />
          Recent activity
          <span className="ml-auto text-[10px] normal-case text-slate-500">last 50 events</span>
        </div>
        <LogPanel events={logs.data?.events ?? []} isLoading={logs.isLoading} />
      </section>

      <OverrideModal
        open={overrideOpen}
        onClose={() => setOverrideOpen(false)}
        current={override}
      />
    </div>
  );
}

type Tone = "ok" | "warn" | "danger";

function StatusCard({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone: Tone;
}) {
  const toneStyle = {
    ok: "border-emerald-700/50 bg-emerald-900/20 text-emerald-200",
    warn: "border-amber-700/50 bg-amber-900/20 text-amber-200",
    danger: "border-rose-700/50 bg-rose-900/20 text-rose-200",
  }[tone];
  const ToneIcon = tone === "ok" ? CheckCircle2 : tone === "warn" ? Circle : AlertTriangle;
  return (
    <div className={`rounded-xl border p-4 ${toneStyle}`}>
      <div className="flex items-center justify-between text-xs uppercase tracking-wide opacity-80">
        <span className="flex items-center gap-1">
          {icon}
          {label}
        </span>
        <ToneIcon className="h-4 w-4 opacity-70" />
      </div>
      <div className="mt-2 text-lg font-semibold">{value}</div>
      {sub ? <div className="mt-0.5 text-xs opacity-70">{sub}</div> : null}
    </div>
  );
}

function formatRelative(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mr = m % 60;
  if (h < 24) return mr ? `${h}h ${mr}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const hr = h % 24;
  return hr ? `${d}d ${hr}h` : `${d}d`;
}

function formatUptime(seconds: number): string {
  return formatRelative(seconds);
}

function evaluatorDisplay(ev: {
  last_result_kind: string | null;
  last_rule_name: string | null;
  last_gate: string | null;
}): string {
  if (!ev.last_result_kind) return "idle";
  switch (ev.last_result_kind) {
    case "rule":
      return `rule · ${ev.last_rule_name ?? "?"}`;
    case "do_nothing":
      return `do_nothing · ${ev.last_rule_name ?? "?"}`;
    case "unmatched":
      return "unmatched";
    case "gate_override":
      return "override active";
    case "gate_pause":
      return "paused";
    case "gate_no_events":
      return "no events";
    case "gate_no_effective":
      return "no effective events";
    default:
      return ev.last_result_kind;
  }
}

function evaluatorTone(ev: { last_result_kind: string | null }): Tone {
  if (!ev.last_result_kind) return "warn";
  if (ev.last_result_kind.startsWith("gate_")) return "warn";
  if (ev.last_result_kind === "unmatched") return "warn";
  return "ok";
}

function LogPanel({ events, isLoading }: { events: LogEvent[]; isLoading: boolean }) {
  if (isLoading && events.length === 0) {
    return <p className="text-sm text-slate-500">Loading…</p>;
  }
  if (events.length === 0) {
    return <p className="text-sm text-slate-500">No recent activity.</p>;
  }
  return (
    <ol className="divide-y divide-slate-800 overflow-hidden rounded-xl border border-slate-800 bg-slate-900/50">
      {events.map((e, idx) => (
        <li key={`${e.ts}-${idx}`} className="flex items-start gap-3 p-3 text-sm">
          <span className={`mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full ${logKindColor(e.kind)}`} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-xs text-slate-400">{e.kind}</span>
              <span className="text-slate-200">{e.message}</span>
            </div>
          </div>
          <span className="flex-shrink-0 font-mono text-[11px] text-slate-500" title={new Date(e.ts * 1000).toLocaleString()}>
            {new Date(e.ts * 1000).toLocaleTimeString()}
          </span>
        </li>
      ))}
    </ol>
  );
}

function logKindColor(kind: string): string {
  if (kind === "led.apply") return "bg-emerald-400";
  if (kind === "led.error") return "bg-rose-400";
  if (kind === "led.skip") return "bg-slate-500";
  if (kind === "override.set" || kind === "override.clear") return "bg-amber-400";
  if (kind === "pause.start" || kind === "pause.stop") return "bg-purple-400";
  if (kind === "webhook.ingest") return "bg-sky-400";
  if (kind === "evaluator.tick") return "bg-indigo-400";
  return "bg-slate-500";
}
